// apps/client/src/services/release.ts
import {
  NewRelease,
  Release,
  ReleaseApproveResponse,
  ReleaseCCPGetResponse,
  ReleaseCCPsListResponse,
  ReleaseChapterGetResponse,
  ReleaseCreateResponse,
  ReleaseGetResponse,
  ReleaseHazardGetResponse,
  ReleaseHazardsListResponse,
  ReleaseListResponse,
  ReleasePageGetResponse,
  ReleaseRejectResponse,
  ReleaseSubmitResponse,
  ReleaseUpdateResponse,
  UpdateRelease,
} from '@fsp/types';
import { apiClient } from './apiClient';

/**
 * Fetch all releases for a plan
 */
export const fetchReleases = async (organisationId: string, planId: string): Promise<Release[]> => {
  const response = await apiClient.get(`/organisations/${organisationId}/plans/${planId}/releases`);

  if (!response.ok) {
    throw new Error(`Failed to fetch releases: ${response.statusText}`);
  }

  const result: ReleaseListResponse = await response.json();
  return result.releases;
};

/**
 * Fetch a specific release
 */
export const fetchRelease = async (
  organisationId: string,
  planId: string,
  releaseId: string
): Promise<ReleaseGetResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch release: ${response.statusText}`);
  }

  const result: ReleaseGetResponse = await response.json();
  return result;
};

/**
 * Create a new release
 */
export const createRelease = async (
  organisationId: string,
  planId: string,
  data: NewRelease
): Promise<Release> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/releases`,
    data
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create release: ${response.statusText}`);
  }

  const result: ReleaseCreateResponse = await response.json();
  return result.release;
};

/**
 * Update a release
 */
export const updateRelease = async (
  organisationId: string,
  planId: string,
  releaseId: string,
  data: UpdateRelease
): Promise<Release> => {
  const response = await apiClient.put(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}`,
    data
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to update release: ${response.statusText}`);
  }

  const result: ReleaseUpdateResponse = await response.json();
  return result.release;
};

/**
 * Delete a release
 */
export const deleteRelease = async (
  organisationId: string,
  planId: string,
  releaseId: string
): Promise<void> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}`
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to delete release: ${response.statusText}`);
  }
};

/**
 * Submit a release for approval
 */
export const submitRelease = async (
  organisationId: string,
  planId: string,
  releaseId: string
): Promise<Release> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/submit`,
    {}
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to submit release: ${response.statusText}`);
  }

  const result: ReleaseSubmitResponse = await response.json();
  return result.release;
};

/**
 * Approve a release
 */
export const approveRelease = async (
  organisationId: string,
  planId: string,
  releaseId: string,
  comments?: string
): Promise<Release> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/approve`,
    { comments }
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to approve release: ${response.statusText}`);
  }

  const result: ReleaseApproveResponse = await response.json();
  return result.release;
};

/**
 * Reject a release
 */
export const rejectRelease = async (
  organisationId: string,
  planId: string,
  releaseId: string,
  comments: string
): Promise<Release> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/reject`,
    { comments }
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to reject release: ${response.statusText}`);
  }

  const result: ReleaseRejectResponse = await response.json();
  return result.release;
};

/**
 * Fetch a specific chapter from a release
 */
export const fetchReleaseChapter = async (
  organisationId: string,
  planId: string,
  releaseId: string,
  chapterId: string
): Promise<ReleaseChapterGetResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/chapters/${chapterId}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch release chapter: ${response.statusText}`);
  }

  const result: ReleaseChapterGetResponse = await response.json();
  return result;
};

/**
 * Fetch a specific page from a release
 */
export const fetchReleasePage = async (
  organisationId: string,
  planId: string,
  releaseId: string,
  chapterId: string,
  pageId: string
): Promise<ReleasePageGetResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/chapters/${chapterId}/pages/${pageId}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch release page: ${response.statusText}`);
  }

  const result: ReleasePageGetResponse = await response.json();
  return result;
};

/**
 * Fetch all hazard snapshots for a release
 */
export const fetchReleaseHazards = async (
  organisationId: string,
  planId: string,
  releaseId: string
): Promise<ReleaseHazardsListResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/hazards`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch release hazards: ${response.statusText}`);
  }

  const result: ReleaseHazardsListResponse = await response.json();
  return result;
};

/**
 * Fetch all CCP snapshots for a release
 */
export const fetchReleaseCCPs = async (
  organisationId: string,
  planId: string,
  releaseId: string
): Promise<ReleaseCCPsListResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/ccps`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch release CCPs: ${response.statusText}`);
  }

  const result: ReleaseCCPsListResponse = await response.json();
  return result;
};

/**
 * Fetch a specific hazard from a release
 */
export const fetchReleaseHazard = async (
  organisationId: string,
  planId: string,
  releaseId: string,
  hazardId: string
): Promise<ReleaseHazardGetResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/hazards/${hazardId}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch release hazard: ${response.statusText}`);
  }

  const result: ReleaseHazardGetResponse = await response.json();
  return result;
};

/**
 * Fetch a specific CCP from a release
 */
export const fetchReleaseCCP = async (
  organisationId: string,
  planId: string,
  releaseId: string,
  ccpId: string
): Promise<ReleaseCCPGetResponse> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/releases/${releaseId}/ccps/${ccpId}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch release CCP: ${response.statusText}`);
  }

  const result: ReleaseCCPGetResponse = await response.json();
  return result;
};
